import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiEye, FiCopy, FiDownload, FiRefreshCw, FiEdit3, FiSave, FiTrash2, FiFileText } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import MeetingMinutesExportModal from './MeetingMinutesExportModal';
import toast from 'react-hot-toast';

const MeetingMinutesOutput = ({ content, originalContent, isGenerating, formData, onContentChange, onRegenerate }) => {
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editableText, setEditableText] = useState(content);
  const textareaRef = useRef(null);

  useEffect(() => {
    setEditableText(content);
  }, [content]);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
    }
  }, [isEditing]);

  const copyToClipboard = async () => {
    if (!content) return;
    try {
      await navigator.clipboard.writeText(editableText);
      toast.success('Meeting minutes copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleSaveEdit = () => {
    onContentChange(editableText);
    setIsEditing(false);
    toast.success('Changes saved!');
  };

  const handleCancelEdit = () => {
    setEditableText(content);
    setIsEditing(false);
  };

  const handleClear = () => {
    onContentChange('');
    setEditableText('');
    toast.success('Content cleared!');
  };

  // Function to format content for display
  const formatContentToHTML = (text) => {
    if (!text) return '';

    let html = text
      // Convert headers
      .replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-purple-500">$1</h1>')
      .replace(/^## (.+)$/gm, '<h2 class="text-xl font-semibold text-gray-800 mb-3 mt-6">$1</h2>')
      .replace(/^### (.+)$/gm, '<h3 class="text-lg font-medium text-gray-700 mb-2 mt-4">$1</h3>')
      // Convert bold text
      .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
      // Convert action items with special styling
      .replace(/^- \[ \] (.+)$/gm, '<div class="flex items-start space-x-2 mb-2 p-2 bg-yellow-50 border-l-4 border-yellow-400 rounded-r"><div class="w-4 h-4 border-2 border-yellow-400 rounded mt-0.5 flex-shrink-0"></div><span class="text-gray-700">$1</span></div>')
      .replace(/^- \[x\] (.+)$/gm, '<div class="flex items-start space-x-2 mb-2 p-2 bg-green-50 border-l-4 border-green-400 rounded-r"><div class="w-4 h-4 bg-green-400 rounded mt-0.5 flex-shrink-0 flex items-center justify-center"><svg class="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path></svg></div><span class="text-gray-700 line-through">$1</span></div>')
      // Convert regular bullet points
      .replace(/^• (.+)$/gm, '<li class="mb-1 text-gray-700">$1</li>')
      // Convert line breaks to paragraphs
      .replace(/\n\n/g, '</p><p class="mb-4 text-gray-700 leading-relaxed">')
      .replace(/\n/g, '<br>');

    // Wrap consecutive list items in ul tags
    html = html.replace(/(<li class="mb-1 text-gray-700">.*?<\/li>)/gs, (match) => {
      const listItems = match.match(/<li class="mb-1 text-gray-700">.*?<\/li>/g);
      if (listItems && listItems.length > 0) {
        return `<ul class="list-disc list-inside mb-4 space-y-1 ml-4">${listItems.join('')}</ul>`;
      }
      return match;
    });

    // Wrap content in paragraphs
    html = `<div class="prose max-w-none"><p class="mb-4 text-gray-700 leading-relaxed">${html}</p></div>`;

    // Clean up empty paragraphs and fix structure
    html = html
      .replace(/<p class="mb-4 text-gray-700 leading-relaxed"><\/p>/g, '')
      .replace(/<p class="mb-4 text-gray-700 leading-relaxed">(<h[1-6])/g, '$1')
      .replace(/(<\/h[1-6]>)<\/p>/g, '$1')
      .replace(/<p class="mb-4 text-gray-700 leading-relaxed">(<ul)/g, '$1')
      .replace(/(<\/ul>)<\/p>/g, '$1')
      .replace(/<p class="mb-4 text-gray-700 leading-relaxed">(<div)/g, '$1')
      .replace(/(<\/div>)<\/p>/g, '$1')
      .replace(/<br><\/p>/g, '</p>')
      .replace(/<p class="mb-4 text-gray-700 leading-relaxed"><br>/g, '<p class="mb-4 text-gray-700 leading-relaxed">');

    return html;
  };

  const LoadingAnimation = () => (
    <div className="flex items-center justify-center py-16">
      <div className="text-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full mx-auto mb-4"
        />
        <p className="text-gray-600 font-medium">Generating meeting minutes...</p>
        <p className="text-sm text-gray-500 mt-1">Analyzing transcript and extracting key information</p>
      </div>
    </div>
  );

  const EmptyState = () => (
    <div className="flex items-center justify-center py-16">
      <div className="text-center">
        <SafeIcon icon={FiFileText} className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500 font-medium">No meeting minutes generated yet</p>
        <p className="text-sm text-gray-400 mt-1">Upload a transcript and click generate</p>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden h-fit">
      <div className="px-6 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold flex items-center">
            <SafeIcon icon={FiEye} className="mr-2" />
            Generated Minutes
          </h2>
          {content && (
            <div className="flex space-x-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={copyToClipboard}
                className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                title="Copy to clipboard"
              >
                <SafeIcon icon={FiCopy} className="h-4 w-4" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsEditing(!isEditing)}
                className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                title={isEditing ? "Save changes" : "Edit content"}
              >
                <SafeIcon icon={isEditing ? FiSave : FiEdit3} className="h-4 w-4" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onRegenerate}
                className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                title="Regenerate minutes"
              >
                <SafeIcon icon={FiRefreshCw} className="h-4 w-4" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsExportModalOpen(true)}
                className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                title="Export options"
              >
                <SafeIcon icon={FiDownload} className="h-4 w-4" />
              </motion.button>
            </div>
          )}
        </div>
      </div>

      <div className="p-6">
        <AnimatePresence mode="wait">
          {isGenerating ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <LoadingAnimation />
            </motion.div>
          ) : content ? (
            <motion.div
              key="content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              {/* Content Display/Edit */}
              {isEditing ? (
                <div className="space-y-3">
                  <textarea
                    ref={textareaRef}
                    value={editableText}
                    onChange={(e) => setEditableText(e.target.value)}
                    className="w-full h-96 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors resize-none font-mono text-sm"
                    placeholder="Edit your meeting minutes..."
                  />
                  <div className="flex space-x-2">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleSaveEdit}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200"
                    >
                      <SafeIcon icon={FiSave} className="h-4 w-4" />
                      <span>Save Changes</span>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleCancelEdit}
                      className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200"
                    >
                      <span>Cancel</span>
                    </motion.button>
                  </div>
                </div>
              ) : (
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-200 shadow-sm max-h-96 overflow-y-auto">
                  <div 
                    className="formatted-content" 
                    dangerouslySetInnerHTML={{ __html: formatContentToHTML(editableText) }} 
                  />
                </div>
              )}

              {/* Action Buttons */}
              {!isEditing && (
                <div className="flex space-x-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={copyToClipboard}
                    className="flex-1 flex items-center justify-center py-2.5 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200"
                  >
                    <SafeIcon icon={FiCopy} className="mr-2 h-4 w-4" />
                    Copy Text
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setIsEditing(true)}
                    className="flex-1 flex items-center justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                  >
                    <SafeIcon icon={FiEdit3} className="mr-2 h-4 w-4" />
                    Edit
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onRegenerate}
                    className="flex-1 flex items-center justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200"
                  >
                    <SafeIcon icon={FiRefreshCw} className="mr-2 h-4 w-4" />
                    Regenerate
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setIsExportModalOpen(true)}
                    className="flex-1 flex items-center justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200"
                  >
                    <SafeIcon icon={FiDownload} className="mr-2 h-4 w-4" />
                    Export
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleClear}
                    className="px-4 py-2.5 border border-red-300 rounded-lg shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
                  >
                    <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                  </motion.button>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <EmptyState />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <MeetingMinutesExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        content={editableText}
        formData={formData}
      />
    </div>
  );
};

export default MeetingMinutesOutput;